import React, { useRef, useState, useEffect } from 'react';
import { X, Check, ArrowLeft, Play, Pause } from 'lucide-react';

interface AudioPreviewModalProps {
  isOpen: boolean;
  audioUrl: string;
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isUploading: boolean;
  showActions?: boolean;
}

const AudioPreviewModal: React.FC<AudioPreviewModalProps> = ({
  isOpen,
  audioUrl,
  fileName,
  onConfirm,
  onCancel,
  isUploading,
  showActions = true
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Reset audio state when modal opens/closes
  useEffect(() => {
    if (isOpen && audioRef.current) {
      audioRef.current.src = audioUrl;
    } else {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [isOpen, audioUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-apple p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Preview Audio File</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <div className="bg-gray-50 rounded-apple p-4">
            <div className="flex items-center space-x-4 mb-3">
              <button
                type="button"
                onClick={togglePlayPause}
                className="w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center hover:bg-primary-600"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
              <div className="flex-1">
                <div className="text-sm text-gray-600">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
                <div
                  className="w-full bg-gray-200 rounded-full h-2 mt-1 relative cursor-pointer"
                  onClick={e => {
                    if (!audioRef.current || duration === 0) return;
                    const rect = (e.target as HTMLDivElement).getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percent = x / rect.width;
                    audioRef.current.currentTime = percent * duration;
                    setCurrentTime(audioRef.current.currentTime);
                  }}
                >
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                  />
                  {/* Thumb indicator */}
                  {duration > 0 && (
                    <div
                      style={{ left: `${(currentTime / duration) * 100}%` }}
                      className="absolute top-[-3px] -translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-2 border-white pointer-events-none"
                    />
                  )}
                </div>
              </div>
            </div>
            <audio
              ref={audioRef}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              className="hidden"
            />
            <div className="text-sm text-gray-600">
              <strong>File:</strong> {fileName}
            </div>
          </div>
        </div>
        
        {showActions && (
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isUploading}
              className="btn btn-primary"
            >
              {isUploading ? (
                'Uploading...'
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Upload
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPreviewModal;
